import axios from "axios";
import { http } from "@/lib/http";
import { isMockEnabled } from "@/lib/mock-flags";
import type { AuthProvider, AuthUser } from "../stores/authStore";
import type { Provider } from "../lib/oauth";

/**
 * Resposta de login/refresh. O refresh token NÃO trafega no corpo — ele vai num
 * cookie HttpOnly `rt` emitido pela API (ADR-0017). O corpo da API devolve
 * `refreshToken: ""`, então ele é opcional/ignorado aqui.
 */
export interface AuthSession {
  accessToken: string;
  user: AuthUser;
}

/** Payload do callback OAuth (PKCE) enviado ao agency-api. */
export interface OAuthCallbackPayload {
  idToken?: string;
  code?: string;
  redirectUri?: string;
  codeVerifier?: string;
}

/** Sessão fake do ambiente de demonstração (espelha o seed do agency-api). */
function mockSession(email: string): AuthSession {
  return {
    accessToken: "mock-jwt-token",
    user: {
      id: "usr_123",
      name: "Dono da Agência",
      email,
      role: "agency_admin",
      agencyId: "ag_456",
      authProvider: "local",
      profileCompleted: true,
      termsAccepted: true,
      approvalStatus: "approved",
    },
  };
}

/**
 * Sessão fake de um dono que entrou AGORA via login social (primeira vez):
 * sem cadastro da imobiliária, sem aceite e aguardando aprovação do backoffice.
 * Exercita o fluxo de onboarding no ambiente de demonstração.
 */
function mockNewSocialSession(provider: AuthProvider): AuthSession {
  return {
    accessToken: `mock-jwt-${provider}`,
    user: {
      id: `usr_${Math.random().toString(36).slice(2, 10)}`,
      name:
        provider === "google"
          ? "Novo Dono (via Google)"
          : provider === "microsoft"
            ? "Novo Dono (via Microsoft)"
            : "Novo Dono (via Apple)",
      email: "novo.dono@imobiliaria.com.br",
      role: "agency_admin",
      agencyId: `ag_${Math.random().toString(36).slice(2, 10)}`,
      authProvider: provider,
      profileCompleted: false,
      termsAccepted: false,
      approvalStatus: "pending",
    },
  };
}

/**
 * Corpo bruto devolvido pela API. O `refreshToken` vem sempre vazio (vai no cookie
 * HttpOnly `rt`) — mantido só para casar o shape do JSON; é descartado no map.
 */
interface AuthSessionWire {
  accessToken: string;
  refreshToken?: string;
  user: AuthUser;
}

function toSession(wire: AuthSessionWire): AuthSession {
  return { accessToken: wire.accessToken, user: wire.user };
}

export async function login(email: string, password: string): Promise<AuthSession> {
  if (isMockEnabled("auth")) {
    await new Promise((resolve) => setTimeout(resolve, 600));
    return mockSession(email);
  }
  const { data } = await http.post<AuthSessionWire>("/auth/login", { email, password });
  return toSession(data);
}

/**
 * Login social: no mock devolve um dono novo (onboarding); no real troca o
 * authorization code (PKCE) pelo agency-api em POST /auth/oauth/{provider}/callback.
 */
export async function loginWithProvider(
  provider: Provider,
  payload: OAuthCallbackPayload = {},
): Promise<AuthSession> {
  if (isMockEnabled("auth")) {
    await new Promise((resolve) => setTimeout(resolve, 700));
    return mockNewSocialSession(provider);
  }
  const { data } = await http.post<AuthSessionWire>(`/auth/oauth/${provider}/callback`, payload);
  return toSession(data);
}

/**
 * Renova a sessão. Sem corpo — o refresh token vai no cookie HttpOnly `rt` enviado
 * automaticamente pelo navegador (withCredentials). A API rotaciona o cookie e
 * devolve um novo access token.
 *
 * Usa um axios "cru" (sem os interceptors do `http`) de propósito: um 401 aqui NÃO
 * pode disparar o fluxo de retry/refresh do interceptor (recursão/deadlock).
 */
export async function refresh(): Promise<AuthSession> {
  const base = import.meta.env.VITE_API_URL ?? "http://localhost:5002/v1";
  const { data } = await axios.post<AuthSessionWire>(`${base}/auth/refresh`, undefined, {
    withCredentials: true,
  });
  return toSession(data);
}

export async function getCurrentUser(): Promise<AuthUser> {
  if (isMockEnabled("auth")) return mockSession("dono@imobiliaria.com.br").user;
  const { data } = await http.get<AuthUser>("/auth/me");
  return data;
}

/** Logout — sem corpo; a API lê e limpa o cookie HttpOnly `rt`. */
export async function logout(): Promise<void> {
  if (isMockEnabled("auth")) return;
  try {
    await http.post("/auth/logout");
  } catch {
    // Logout local é o que importa; falha de rede no revoke é tolerável.
  }
}

// ─── Cadastro self-serve + ativação por convite ────────────────────────────────
// A imobiliária nasce SEMPRE pendente (modelo híbrido): register/activate devolvem
// o STATUS (sem sessão); a tela /pending consulta por e-mail até o backoffice
// liberar. Só `approved` permite logar e acessar o portal.

export type RegistrationStatusValue = "pending" | "approved" | "rejected";

export interface RegistrationStatus {
  status: RegistrationStatusValue;
  email: string;
  agencyName?: string | null;
  rejectionReason?: string | null;
}

export interface RegisterInput {
  agencyName: string;
  cnpj: string;
  contactName: string;
  email: string;
  phone: string;
  password: string;
  requestedPlanCode?: string;
}

export interface ActivateAccountInput {
  token: string;
  password: string;
}

/** E-mail de demonstração que volta "recusado" no mock (exercita a tela de recusa). */
const MOCK_REJECTED_EMAIL = "recusado@imobiliaria.com.br";

function mockStatusFor(email: string): RegistrationStatus {
  return email.trim().toLowerCase() === MOCK_REJECTED_EMAIL
    ? { status: "rejected", email, rejectionReason: "Dados cadastrais não conferem. Procure o suporte." }
    : { status: "pending", email };
}

export async function register(input: RegisterInput): Promise<RegistrationStatus> {
  if (isMockEnabled("auth")) {
    await new Promise((resolve) => setTimeout(resolve, 700));
    return { status: "pending", email: input.email, agencyName: input.agencyName };
  }
  const { data } = await http.post<RegistrationStatus>("/auth/register", input);
  return data;
}

export async function getRegistrationStatus(email: string): Promise<RegistrationStatus> {
  if (isMockEnabled("auth")) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return mockStatusFor(email);
  }
  const { data } = await http.get<RegistrationStatus>("/auth/registration-status", {
    params: { email },
  });
  return data;
}

export async function activateAccount(input: ActivateAccountInput): Promise<RegistrationStatus> {
  if (isMockEnabled("auth")) {
    await new Promise((resolve) => setTimeout(resolve, 700));
    return { status: "pending", email: "convidada@imobiliaria.com.br" };
  }
  const { data } = await http.post<RegistrationStatus>("/auth/activate-account", input);
  return data;
}
